import React, { useState } from 'react'
import {
  claimReachLabel,
  factIsFlagged,
  groupClaimsByOrigin,
  type UnderstandingClaim,
} from './cfoUnderstanding'
import { normalizeCurrencyTextCompact } from './cfoFormat'
import './CfoUnderstandingPanel.css'

// The canned instruction sent when the user clicks "Add the original back" on a de-prioritized
// (demoted) metric — it asks the AI to keep the original metric visible rather than hiding it.
const ADD_ORIGINAL_BACK_INSTRUCTION =
  'Keep showing the original metric alongside the replacement — do not hide it.'

// A fact is an AI *replacement* (a de-prioritized metric) when its lead claim is a metric band
// override whose directive payload demotes the metric in favour of another. We read the human
// `replacement_label` off the directive payload to tell the user what to focus on instead.
function demoteReplacementLabel(lead: UnderstandingClaim): string | null {
  if (lead.directiveKind !== 'metric.band_override') return null
  const json = lead.directiveJson
  if (!json || typeof json !== 'object') return null
  const obj = json as Record<string, unknown>
  if (obj.action !== 'demote') return null
  const label = obj.replacement_label
  return typeof label === 'string' && label.length > 0 ? label : null
}

// Whether a fact is a demote replacement at all (independent of having a replacement_label) — used
// to decide whether to offer the "Add the original back" action.
function isDemoteReplacement(lead: UnderstandingClaim): boolean {
  if (lead.directiveKind !== 'metric.band_override') return false
  const json = lead.directiveJson
  if (!json || typeof json !== 'object') return false
  return (json as Record<string, unknown>).action === 'demote'
}

// CfoUnderstandingPanel (plan Part 4): renders the per-client belief ledger as a list of "facts".
// A fact is an `originId` group whose directives may drive the ratios (`metric.*`) and/or the forecast
// (`forecast.*`) — the panel makes that dual reach visible so one belief, and later one redirect,
// is understood to move both lenses. Presentational + self-contained: it takes already-fetched claims
// and never calls the network itself (the page wrapper does the fetch), so it is trivially testable.

export type CfoUnderstandingPanelProps = {
  claims: UnderstandingClaim[]
  loading?: boolean
  error?: string | null
  /** Optional heading; omit to render just the ledger body (e.g. when embedded in a drawer). */
  heading?: string | null
  /** Governance callbacks (plan B3). When omitted, the panel is read-only (no action row). */
  onConfirm?: (claimId: string, originId: string) => void
  onReject?: (claimId: string, originId: string) => void
  onRedirect?: (originId: string, instruction: string) => void
  /** The fact group whose mutation is in-flight; its action row is disabled. */
  busyOriginId?: string | null
  /**
   * When `false`, the served forecast was flagged cash-implausible (its projected cash crosses below
   * the client's own historical floor) and the AI is re-diagnosing it on the next close — a calm
   * "under review" banner is shown. `undefined`/`true` = no banner (the common, healthy case).
   */
  forecastCashPlausible?: boolean
}

const SOURCE_LABELS: Record<string, string> = {
  ai_inferred: 'AI inferred',
  ai_assumed: 'AI assumed',
  ai_revised: 'AI revised',
  user_confirmed: 'You confirmed',
  analyst_authored: 'Analyst',
}

const sourceLabel = (source: string): string => SOURCE_LABELS[source] ?? source

const statusLabel = (status: string): string =>
  status ? status.charAt(0).toUpperCase() + status.slice(1) : status

const confidencePct = (confidence: number | null): string | null =>
  confidence == null ? null : `${Math.round(confidence * 100)}%`

// Beliefs that are surfaced in the ledger: live (`active`), awaiting the believability gate
// (`proposed`/`held`). `rejected`/`superseded` are history, not shown in the active ledger.
const VISIBLE_STATUSES = new Set(['active', 'proposed', 'held'])

export function CfoUnderstandingPanel({
  claims,
  loading = false,
  error = null,
  heading = 'AI Tuning — what FuseGrid changed, and why',
  onConfirm,
  onReject,
  onRedirect,
  busyOriginId = null,
  forecastCashPlausible,
}: CfoUnderstandingPanelProps) {
  // The served forecast was flagged cash-implausible -> a calm "under review" banner. Explicit `false`
  // only (undefined = unknown/not wired = no banner).
  const forecastUnderReview = forecastCashPlausible === false
  const interactive = Boolean(onConfirm || onReject || onRedirect)
  // The fact (originId) whose inline "Tell the AI…" textarea is open, plus its draft text. Only one
  // fact is being adjusted at a time, so a single pair of state slots suffices.
  const [adjustingOrigin, setAdjustingOrigin] = useState<string | null>(null)
  const [adjustText, setAdjustText] = useState('')

  const openAdjust = (originId: string) => {
    setAdjustingOrigin(originId)
    setAdjustText('')
  }
  const cancelAdjust = () => {
    setAdjustingOrigin(null)
    setAdjustText('')
  }
  const sendAdjust = (originId: string) => {
    const text = adjustText.trim()
    if (!text) return
    onRedirect?.(originId, text)
    cancelAdjust()
  }

  if (loading) {
    return (
      <div className="cu-panel cu-panel--state" role="status">
        <span className="cu-spinner" aria-hidden="true" />
        Learning your business…
      </div>
    )
  }
  if (error) {
    return (
      <div className="cu-panel cu-panel--state cu-panel--error" role="alert">
        {error}
      </div>
    )
  }

  const underReviewBanner = forecastUnderReview ? (
    <div className="cu-under-review" role="status" data-testid="cu-under-review">
      <span className="cu-under-review-dot" aria-hidden="true" />
      This forecast is under review — FuseGrid flagged the projected cash as implausibly low (below your
      business's historical range) and is re-checking it on your next month-end close.
    </div>
  ) : null

  const visible = claims.filter((claim) => VISIBLE_STATUSES.has(claim.status))
  if (visible.length === 0) {
    return (
      <div className="cu-panel cu-panel--empty">
        {underReviewBanner}
        <p className="cu-empty-title">Still learning your business</p>
        <p className="cu-empty-body">
          As FuseGrid analyzes your books, the beliefs that shape your forecast and your ratios will
          appear here — each with its evidence, and one tap to agree or redirect.
        </p>
      </div>
    )
  }

  const groups = Array.from(groupClaimsByOrigin(visible).entries())

  return (
    <div className="cu-panel">
      {heading && <h2 className="cu-heading">{heading}</h2>}
      {underReviewBanner}
      <ul className="cu-fact-list">
        {groups.map(([originId, group]) => {
          const lead = group[0]
          const reach = claimReachLabel(group)
          const conf = confidencePct(lead.confidence)
          const flagged = factIsFlagged(group)
          const replacementLabel = demoteReplacementLabel(lead)
          const isReplacement = isDemoteReplacement(lead)
          const adjusting = adjustingOrigin === originId
          return (
            <li
              key={originId}
              className={`cu-fact${flagged ? ' cu-fact--flagged' : ''}`}
              data-testid="cu-fact"
              data-flagged={flagged ? 'true' : undefined}
            >
              <div className="cu-fact-head">
                <span className="cu-assertion">{normalizeCurrencyTextCompact(lead.assertion)}</span>
                <span className={`cu-pill cu-pill--source cu-source-${lead.source}`}>
                  {sourceLabel(lead.source)}
                </span>
              </div>
              <div className="cu-fact-meta">
                <span className="cu-reach" data-testid="cu-reach">
                  {reach}
                </span>
                {conf && <span className="cu-confidence">confidence {conf}</span>}
                <span className="cu-status" data-status={lead.status}>
                  {statusLabel(lead.status)}
                </span>
                {flagged && (
                  <span
                    className="cu-pill cu-pill--flagged"
                    data-testid="cu-flagged"
                    title="Auto-applied by the AI but worth a second look — agree to keep it, or redirect to correct it."
                  >
                    Flagged for review
                  </span>
                )}
              </div>
              {group.length > 1 && (
                <ul className="cu-directive-list">
                  {group.map((claim) => (
                    <li key={claim.id} className="cu-directive">
                      <code className="cu-directive-kind">{claim.directiveKind}</code>
                      <span className="cu-directive-status" data-status={claim.status}>
                        {statusLabel(claim.status)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {replacementLabel && (
                <p className="cu-replacement" data-testid="cu-replacement">
                  Replaced — focus on {normalizeCurrencyTextCompact(replacementLabel)} instead
                </p>
              )}
              {interactive && (
                <div className="cu-fact-actions" data-testid="cu-fact-actions">
                  <button
                    type="button"
                    className="cu-action cu-action--agree"
                    disabled={busyOriginId === originId}
                    onClick={() => onConfirm?.(lead.id, originId)}
                  >
                    Agree
                  </button>
                  <button
                    type="button"
                    className="cu-action cu-action--disagree"
                    disabled={busyOriginId === originId}
                    onClick={() => onReject?.(lead.id, originId)}
                  >
                    Disagree
                  </button>
                  {isReplacement && (
                    <button
                      type="button"
                      className="cu-action cu-action--addback"
                      disabled={busyOriginId === originId}
                      onClick={() => onRedirect?.(originId, ADD_ORIGINAL_BACK_INSTRUCTION)}
                    >
                      Add the original back
                    </button>
                  )}
                  <button
                    type="button"
                    className="cu-action cu-action--redirect"
                    disabled={busyOriginId === originId || adjusting}
                    onClick={() => openAdjust(originId)}
                  >
                    Tell the AI…
                  </button>
                </div>
              )}
              {interactive && adjusting && (
                <div className="cu-adjust" data-testid="cu-adjust">
                  <textarea
                    className="cu-adjust-text"
                    data-testid="cu-adjust-text"
                    placeholder="How should FuseGrid rethink this belief?"
                    value={adjustText}
                    onChange={(e) => setAdjustText(e.target.value)}
                    autoFocus
                  />
                  <div className="cu-adjust-actions">
                    <button
                      type="button"
                      className="cu-action cu-action--send"
                      disabled={busyOriginId === originId || adjustText.trim().length === 0}
                      onClick={() => sendAdjust(originId)}
                    >
                      Send
                    </button>
                    <button
                      type="button"
                      className="cu-action cu-action--cancel"
                      disabled={busyOriginId === originId}
                      onClick={cancelAdjust}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default CfoUnderstandingPanel
